import { useCallback, useMemo, useState } from 'react';

import { EVENT_TYPE_COLORS, type EventResponse, EVENTS } from '@/store/events/events.dto';
import { type TaskResponse, TaskTypes } from '@/store/tasks/tasks.dto';

export type CalendarItemType = 'all' | 'events' | 'tasks';

export type CalendarKind = 'events' | 'trips' | 'tasks' | 'maintenance';

export const CALENDAR_KINDS: { id: CalendarKind; label: string; color: string }[] = [
  { id: 'events', label: 'Event', color: EVENT_TYPE_COLORS[EVENTS.GENERAL] },
  { id: 'trips', label: 'Trip', color: EVENT_TYPE_COLORS[EVENTS.TRIP] },
  { id: 'tasks', label: 'Task', color: 'var(--accent-details-2)' },
  { id: 'maintenance', label: 'Maintenance', color: EVENT_TYPE_COLORS[EVENTS.OUT_OF_WATER] },
];

export type CalendarFilters = {
  startDate: string;
  endDate: string;
  itemType: CalendarItemType;
  kinds?: CalendarKind[];
  withGuests?: boolean;
  withMeals?: boolean;
  /** Events carrying any of these tags. Tasks carry none, so they drop out. */
  tagIds?: string[];
};

export type CalendarEventItem = EventResponse & { item_kind?: 'event' };
export type CalendarTaskItem = TaskResponse & { item_kind?: 'task' };
export type CalendarAbsenceItem = {
  id: string;
  item_kind: 'absence';
  personId: string;
  personName: string;
  title: string;
  time_slot: { start: string; end: string; all_day?: boolean };
  type: 'absence';
};
export type CalendarItem = CalendarEventItem | CalendarTaskItem | CalendarAbsenceItem;

const withOpenEndUntilToday = (event: EventResponse): EventResponse => {
  if (!event.time_slot?.start || event.time_slot.end) return event;

  const start = new Date(event.time_slot.start);
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  if (Number.isNaN(start.getTime()) || start > endOfToday) return event;

  return { ...event, time_slot: { ...event.time_slot, end: endOfToday.toISOString() } };
};

export const toCalendarItems = (
  events: EventResponse[] = [],
  tasks: TaskResponse[] = [],
): CalendarItem[] => [
  ...events.map((event) => ({ ...withOpenEndUntilToday(event), item_kind: 'event' as const })),
  ...tasks.map((task) => ({ ...task, item_kind: 'task' as const })),
];

const TASK_TYPES_VALUES = Object.values(TaskTypes) as string[];

export const isCalendarTask = (item: CalendarItem): item is CalendarTaskItem =>
  item.item_kind ? item.item_kind === 'task' : TASK_TYPES_VALUES.includes(item.type);

export const isCalendarAbsence = (item: CalendarItem): item is CalendarAbsenceItem =>
  item.item_kind === 'absence';

const isTask = isCalendarTask;

const isInDateRange = (
  itemStart: string | undefined,
  itemEnd: string | undefined,
  filterStart: string,
  filterEnd: string,
): boolean => {
  if (!itemStart) return false;

  const itemStartDate = new Date(itemStart);
  const itemEndDate = itemEnd ? new Date(itemEnd) : itemStartDate;

  const hasStartFilter = !!filterStart;
  const hasEndFilter = !!filterEnd;

  // Case 1: Both start and end filters - Range mode (show overlapping events)
  if (hasStartFilter && hasEndFilter) {
    const filterStartDate = new Date(filterStart);
    const filterEndDate = new Date(filterEnd);

    // Event must intersect with the filter range
    if (itemEndDate < filterStartDate) return false;
    if (itemStartDate > filterEndDate) return false;

    return true;
  }

  // Case 2: Only start filter - Strict mode (event must start after filter start)
  if (hasStartFilter && !hasEndFilter) {
    const filterStartDate = new Date(filterStart);
    return itemStartDate >= filterStartDate;
  }

  // Case 3: Only end filter - Strict mode (event must end before filter end)
  if (!hasStartFilter && hasEndFilter) {
    const filterEndDate = new Date(filterEnd);
    return itemEndDate <= filterEndDate;
  }

  return true;
};

const defaultFilters: CalendarFilters = {
  startDate: '',
  endDate: '',
  itemType: 'all',
};

const kindOf = (item: CalendarItem): CalendarKind => {
  if (!isTask(item)) return item.type === EVENTS.TRIP ? 'trips' : 'events';
  return item.equipment ? 'maintenance' : 'tasks';
};

const hasGuests = (item: CalendarItem): boolean => {
  if (isTask(item) || isCalendarAbsence(item)) return false;
  return (item.participants ?? []).some((p) => p.type === 'guest');
};

const hasAnyTag = (item: CalendarItem, tagIds: string[]): boolean => {
  if (isTask(item) || isCalendarAbsence(item)) return false;
  return (item.tags ?? []).some((tag) => tagIds.includes(tag.id));
};

const hasMeals = (item: CalendarItem): boolean => {
  if (isCalendarAbsence(item)) return false;
  if (item.has_menu) return true;
  return !isTask(item) && (item.sub_activities ?? []).some((sub) => sub.has_menu);
};

export const useCalendarFilter = (items: CalendarItem[]) => {
  const [filters, setFilters] = useState<CalendarFilters>(defaultFilters);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      // Absence bands are always shown to eligible viewers; no filter applies to them.
      if (isCalendarAbsence(item)) return true;

      const itemIsTask = isTask(item);

      // Filter by item type
      if (filters.itemType === 'events' && itemIsTask) return false;
      if (filters.itemType === 'tasks' && !itemIsTask) return false;

      if (filters.kinds && !filters.kinds.includes(kindOf(item))) return false;
      if (filters.withGuests && !hasGuests(item)) return false;
      if (filters.withMeals && !hasMeals(item)) return false;
      if (filters.tagIds?.length && !hasAnyTag(item, filters.tagIds)) return false;

      // Filter by date range (applies to both events and tasks)
      if (filters.startDate || filters.endDate) {
        const inRange = isInDateRange(
          item.time_slot?.start,
          item.time_slot?.end,
          filters.startDate,
          filters.endDate,
        );
        if (!inRange) return false;
      }

      return true;
    });
  }, [items, filters]);

  const updateFilters = useCallback((newFilters: Partial<CalendarFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.startDate !== '' ||
      filters.endDate !== '' ||
      filters.itemType !== 'all' ||
      (filters.kinds?.length ?? CALENDAR_KINDS.length) < CALENDAR_KINDS.length ||
      !!filters.withGuests ||
      !!filters.withMeals ||
      !!filters.tagIds?.length
    );
  }, [filters]);

  return {
    filters,
    filteredItems,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    resultCount: filteredItems.length,
  };
};
